// Copyright (c) StrangeBee 2024

import React, { ReactElement, ReactNode, useContext } from 'react';
import FormatDate from '@Molecules/Format/FormatDate/FormatDate';
import DisplayTag from '@Organisms/DisplayTag/DisplayTag';
import SecurityTag from '@Atoms/Info/SecurityTag/SecurityTag';
import { SecurityTagType } from '@Types';
import { useListAllUsers } from '@Queries/userQueries';
import { SystemUser } from '@Constants/organisation';
import { findUser } from '@Helpers/findUser';
import { required } from '@Molecules/Formidable/Rules';
import { CaseCustomField, CaseOutput, ObservableReportLevel } from 'thehive-sdk';
import { cutString } from '@Helpers/string';
import { DisplayCustomFields } from '@Organisms/DisplayCustomFields/DisplayCustomFields';
import { convertToLocaleId, useLocalMessage } from '@Helpers/locale';
import { ScopeContext } from '@Context/ScopeProvider';
import { ColorLabel } from '@Atoms/Info/ColorLabel/ColorLabel';
import { HTMLColors, ObservableReportLevelColors } from '@Constants/colors';
import { classList } from '@Helpers/className';
import { Link } from 'react-router-dom';
import { useStateValue } from '@Context/StateProvider';
import { formatFileSize } from '@Helpers/stats';
import THTag from '@Atoms/THTag/THTag';

import './PropRenderer.scss';

interface PropRenderProps {
    prop: string;
    value: any;
    type?: string;
}

interface RenderingProps {
    type?: string[];
    match?: (v: string) => boolean;
    render: (v: any) => ReactNode | null;
}

const colorStatus: Record<string, string> = {
    Success: 'green',
    Failure: 'volcano',
    default: 'lightgray',
};

export const PropRender = ({ prop, value, type = '-' }: PropRenderProps): ReactElement | null => {
    const { data: allUsersList = [] } = useListAllUsers();
    const [{ targetWindow }] = useStateValue();

    const local = useLocalMessage();
    const {
        customStatus: {
            alert: { getStatus: getStatusAlert },
            case: { getStatus: getStatusCase },
        },
    } = useContext(ScopeContext);

    const Rendering: RenderingProps[] = [
        {
            type: ['status'],
            render: (value: string) => {
                const statusConf = type === 'case' ? getStatusCase(value) : getStatusAlert(value);

                return ['case', 'alert'].includes(type) ? (
                    <ColorLabel
                        label={local({
                            id: `${type}.data.status.${convertToLocaleId(statusConf?.value || '')}`,
                            defaultMessage: statusConf?.value,
                        })}
                        color={statusConf?.colour || HTMLColors.DARK_GRAY}
                        className={classList('status-tag')}
                        type="fill"
                    />
                ) : (
                    <ColorLabel
                        label={value}
                        color={colorStatus[value] || colorStatus.default}
                        className={classList('status-tag')}
                        type="fill"
                    />
                );
            },
        },
        {
            type: ['tags'],
            render: (values: any) => (
                <span className="stream-card__tags">
                    <DisplayTag.List data={values} limit={5}>
                        {(value: string) => <DisplayTag tag={value} />}
                    </DisplayTag.List>
                </span>
            ),
        },
        {
            type: ['owner'],
            render: (value: any) => {
                const author = value !== SystemUser.login ? findUser(value, allUsersList) : SystemUser;
                const { name = '?' } = author || {};

                return <span className="prop-render__value-brut">{name}</span>;
            },
        },
        {
            type: ['severity', 'tlp', 'pap'],
            render: (value: any) => <SecurityTag type={prop as SecurityTagType} value={value || 0} />,
        },
        {
            type: ['startDate', 'dueDate', 'endDate', 'date'],
            render: (value: any) => <FormatDate date={value || 0} />,
        },
        {
            type: ['size'],
            render: (value = 0) => <span className="prop-render__value-brut">{formatFileSize(value)}</span>,
        },
        {
            type: ['share', 'unshare'],
            render: ({ organisation = '', profile = '' }: any) => (
                <span className="prop-render__value-brut">{`${organisation} ${profile ? ` (${profile})` : ``}`}</span>
            ),
        },
        {
            type: ['observable'],
            render: ({ title, path }) => {
                return path ? (
                    <Link to={path} target={targetWindow} className="stream-header__link">
                        {title}
                    </Link>
                ) : (
                    title
                );
            },
        },
        {
            type: ['customFields'],
            render: (customFields: CaseCustomField[]) => (
                <span className="stream-card__custom-fields">
                    <DisplayCustomFields customFields={customFields || []} />
                </span>
            ),
        },
        {
            type: ['fromAlert'],
            render: (value: Record<string, Record<string, any>>) => {
                return (
                    <span className="prop-render__value-from-alert">
                        {Object.entries(value || {}).map(([key, value]) => (
                            <PropRender key={key} prop={key} value={value} type="alert" />
                        ))}
                    </span>
                );
            },
        },
        {
            type: ['description', 'message', 'title'],
            render: (value: any) => <span>{cutString(value, 140)}</span>,
        },
        {
            type: ['cases'],
            render: (cases: Pick<CaseOutput, 'number' | 'title' | '_id'>[]) => (
                <>
                    {cases?.map(({ number, title, _id }) => (
                        <THTag value={`#${number} - ${title}`} key={_id} />
                    ))}
                </>
            ),
        },
    ];

    if (required(value)) {
        return null;
    }

    const Elem = Rendering.find((process) => process?.type?.includes(prop) || process?.match?.(prop))?.render(value);

    const Value =
        prop === 'reports'
            ? Object.entries(value).map(([, val]: [string, any]) => {
                  if (val && val.taxonomies) {
                      return val.taxonomies.map((taxonomy: { text: string; level: string }) => (
                          <ColorLabel
                              key={taxonomy.text}
                              label={taxonomy.text}
                              color={
                                  ObservableReportLevelColors[taxonomy.level as ObservableReportLevel]
                                      ? ObservableReportLevelColors[taxonomy.level as ObservableReportLevel]
                                      : ObservableReportLevelColors.info
                              }
                              className={classList(['status-tag', 'reports'])}
                              type="fill"
                          />
                      ));
                  }
                  return null;
              })
            : Elem || value?.toString();

    return (
        <>
            <span className="prop-render__key">{prop}</span>
            <span className="prop-render__value">
                <span className="prop-render__value-brut">{Value}</span>
            </span>
        </>
    );
};
